import { useState, useEffect, useRef } from 'react';
import { searchTracks, getCatalog } from '../../api/spotify.api';

const MAX_LOCAL_RESULTS = 50;

const normalize = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

// Búsqueda local sobre la playlist VIP: todas las palabras deben aparecer en título o artista.
export function filterTracks(tracks, q) {
  const words = normalize(q).split(/\s+/).filter(Boolean);
  return tracks
    .filter((t) => {
      const haystack = normalize(`${t.name} ${t.artist}`);
      return words.every((w) => haystack.includes(w));
    })
    .slice(0, MAX_LOCAL_RESULTS);
}

// mode 'playlist': la sala solo acepta canciones de la playlist del local; se descarga una vez
// y se busca en memoria (sin llamadas a Spotify). Cualquier otro modo busca en Spotify.
export function useSearch(roomId, mode) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [playlistName, setPlaylistName] = useState(null);
  const catalog = useRef(null); // promesa del catálogo, compartida por todas las búsquedas

  useEffect(() => {
    if (mode !== 'playlist') return;
    catalog.current = getCatalog(roomId);
    catalog.current.then((c) => setPlaylistName(c.playlist?.name ?? null)).catch(() => {});
  }, [roomId, mode]);

  const search = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      if (mode === 'playlist') {
        catalog.current ??= getCatalog(roomId);
        // Si la descarga falló, se reintenta en la próxima búsqueda.
        const { tracks } = await catalog.current.catch((err) => { catalog.current = null; throw err; });
        setResults(filterTracks(tracks, q));
      } else {
        setResults(await searchTracks(roomId, q));
      }
    } catch (err) {
      setError(err.response?.data?.message ?? (err.response ? 'Error al buscar' : 'La búsqueda tardó demasiado. Intenta de nuevo.'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setResults([]); setError(null); setHasSearched(false); };

  return { results, loading, error, hasSearched, playlistName, search, clear };
}
